import { createContext, ReactNode, useCallback,useContext, useState } from "react";

import { Thread } from "../types/thread";

interface MobileChatHistoryContextType {
  // Visibility state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleOpen: () => void;

  // Data from CoPilot
  threads: Thread[];
  activeThreadId: string | null;
  isLoading: boolean;

  // Handlers from CoPilot
  onThreadSelect: ((threadId: string) => void) | null;
  onThreadDelete: ((threadId: string) => void) | null;
  onNewThread: (() => void) | null;

  // Register data and handlers (called by CoPilot)
  registerChatData: (data: {
    threads: Thread[];
    activeThreadId: string | null;
    isLoading: boolean;
    onThreadSelect: (threadId: string) => void;
    onThreadDelete: (threadId: string) => void;
    onNewThread: () => void;
  }) => void;

  // Unregister (called when leaving chat page)
  unregisterChatData: () => void;

  // Whether chat history should be shown (data is registered)
  isRegistered: boolean;
}

const MobileChatHistoryContext = createContext<MobileChatHistoryContextType | null>(null);

export const MobileChatHistoryProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [handlers, setHandlers] = useState<{
    onThreadSelect: ((threadId: string) => void) | null;
    onThreadDelete: ((threadId: string) => void) | null;
    onNewThread: (() => void) | null;
  }>({
    onThreadSelect: null,
    onThreadDelete: null,
    onNewThread: null,
  });

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const registerChatData = useCallback((data: {
    threads: Thread[];
    activeThreadId: string | null;
    isLoading: boolean;
    onThreadSelect: (threadId: string) => void;
    onThreadDelete: (threadId: string) => void;
    onNewThread: () => void;
  }) => {
    setThreads(data.threads);
    setActiveThreadId(data.activeThreadId);
    setIsLoading(data.isLoading);
    setHandlers({
      onThreadSelect: data.onThreadSelect,
      onThreadDelete: data.onThreadDelete,
      onNewThread: data.onNewThread,
    });
    setIsRegistered(true);
  }, []);

  const unregisterChatData = useCallback(() => {
    setIsRegistered(false);
    setIsOpen(false);
    setThreads([]);
    setActiveThreadId(null);
    setIsLoading(false);
    setHandlers({
      onThreadSelect: null,
      onThreadDelete: null,
      onNewThread: null,
    });
  }, []);

  return (
    <MobileChatHistoryContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleOpen,
        threads,
        activeThreadId,
        isLoading,
        onThreadSelect: handlers.onThreadSelect,
        onThreadDelete: handlers.onThreadDelete,
        onNewThread: handlers.onNewThread,
        registerChatData,
        unregisterChatData,
        isRegistered,
      }}
    >
      {children}
    </MobileChatHistoryContext.Provider>
  );
};

export const useMobileChatHistory = () => {
  const context = useContext(MobileChatHistoryContext);
  if (!context) {
    throw new Error("useMobileChatHistory must be used within MobileChatHistoryProvider");
  }
  return context;
};
